import RecruitChip from '../components/RecruitChip';
import PmBlock from '../components/PmBlock';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import ReferralBoard from '../components/ReferralBoard';
import TasksTab from '../components/TasksTab';
import TaskBanners from '../components/TaskBanners';
import useTaskBadge from '../hooks/useTaskBadge';
import { hashTaskId } from '../lib/tasks';
import { Topbar, NavTabs, Card, SectionTitle, Loader, SaveBanner, Btn, Modal, Input } from '../components/UI';
import { getStatusColor, statusShort, statusSign, STATUSES } from '../components/StatusPicker';
import { rememberRoutes } from '../lib/data';
import { getActivePeriod, getSvIdByUser, getSvIdsByUser, getStatusesBySv, setStatus, clearStatus, getVacationsBySv, getOrdersByRoutes, setOrderVisits, getHyperTabellBySv, setHyperPlan, getRowsByRouteChunks } from '../lib/data';
import SvPlanner from '../components/SvPlanner';
import { exportTabellToExcel } from '../lib/exportExcel';
import { supabase } from '../lib/supabase';
import VacationCalendar from '../components/VacationCalendar';
import GameBar from '../components/GameBar';
import { getGameSettings, checkWeekForecast } from '../lib/game';
import { mskToday } from '../lib/alerts';

const DOW_RU = ['вс','пн','вт','ср','чт','пт','сб'];
const MONTHS_RU = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
// Короткое название месяца из ISO-даты 'YYYY-MM-DD' (чтобы подписи не были зашиты «сен»).
function moShort(iso) { return iso ? MONTHS_RU[parseInt(String(iso).slice(5, 7), 10) - 1] || '' : ''; }
const WEEK_COLORS = {
  36: { hdr: '#2E75B6', light: '#D6E4F0' },
  37: { hdr: '#70AD47', light: '#E2EFDA' },
  38: { hdr: '#FFC000', light: '#FFF2CC' },
  39: { hdr: '#F4B183', light: '#FCE4D6' },
  40: { hdr: '#808080', light: '#EDEDED' },
};

// Merchandisers может прийти как массив или как объект (зависит от unique constraint в БД)
function getMrc(route) {
  const m = route.merchandisers;
  if (!m) return null;
  return Array.isArray(m) ? m[0] : m;
}

function isWeekend(dow) { return dow === 0 || dow === 6; }

function getWeeksForPeriod(period) {
  if (!period?.work_weeks) return {};
  const weeks = {};
  period.work_weeks.forEach(w => {
    const days = [];
    const from = new Date(w.date_from);
    const to   = new Date(w.date_to);
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      days.push({ day: d.getDate(), dow: d.getDay(), date: d.toISOString().split('T')[0] });
    }
    weeks[w.week_number] = { ...w, days };
  });
  return weeks;
}

const OVERLAP = new Set(['БЛ+МР','БЛ+ПМ','БЛ+ВЫХ','ОТ+МР','ОТ+ПМ','ОТ+ВЫХ','ДО+МР','ДО+ПМ','ДО+ВЫХ','ВК+МР','ВК+ПМ','ВК+ВЫХ']);

function calcMetrics(statuses, routeId, weekDays) {
  let exits = 0, sick = 0, vacation = 0, dayoff = 0, vacancy = 0, overlaps = 0;
  weekDays.forEach(({ date }) => {
    const s = statuses[routeId]?.[date];
    if (!s) return;
    if (s === '1' || s === 'СВ') exits++;
    if (s === 'БЛ-') sick++;
    if (s === 'ОТ-') vacation++;
    if (s === 'ДО-') dayoff++;
    if (s === 'ВК-') vacancy++;
    if (OVERLAP.has(s)) overlaps++;
  });
  return { exits, sick, vacation, dayoff, vacancy, overlaps };
}

export default function SvPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(() => hashTaskId() ? 6 : 0);
  const [taskN, refreshTaskN] = useTaskBadge(user.id);
  // #3 — общая для всех вкладок выбранная неделя и режим, чтобы не сбрасывались при переключении
  const [wIdx, setWIdx]         = useState(0);
  const [tabMode, setTabMode]   = useState('week');
  const [period, setPeriod]     = useState(null);
  const [routes, setRoutes]     = useState([]);
  const [statuses, setStatuses] = useState({});
  const [orders, setOrders] = useState({});       // { route_id: { date: { file_visits, visits } } }
  const [statusesV, setStatusesV] = useState({}); // { route_id: { date: { code, plan_visits } } }
  const [factsMap, setFactsMap] = useState({});   // { route_id: { date: visits_fact } }
  const [hyperData, setHyperData] = useState({ points: [], daily: {}, routesById: {} });
  const [vacations, setVacations] = useState([]);
  const [servicePct, setServicePct] = useState({}); // { route_id: { week_number: pct } }
  const [visitFacts, setVisitFacts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [vacancyFill, setVacancyFill] = useState(null); // { routeId, code }
  const [vacancyName, setVacancyName] = useState('');
  const [vacancySaving, setVacancySaving] = useState(false);
  const [saveBanner, setSaveBanner] = useState({ show: false, msg: '' });
  const [game, setGame] = useState(null); // настройки игры (null — ещё не загружены / выключено)

  // Найдём sv_id из базы по user_id
  const [svId, setSvId] = useState(null);
  const [svIds, setSvIds] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    loadData();
    // Игра грузится отдельно и никогда не мешает табелю: ошибка = кнопки просто нет.
    getGameSettings().then(setGame, () => setGame(null));
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const p = await getActivePeriod();
      setPeriod(p);

      // Найдём supervisor по user_id (с ретраями; сбой ≠ «не привязан»)
      setLoadError(false);
      const foundSvId = await getSvIdByUser(user.id);
      if (!foundSvId) { setLoading(false); return; }
      const sv = { id: foundSvId };
      setSvId(sv.id);
      // все записи СВ учётки (маршруты, разнесённые по разным ТМ)
      let allSvIds = [sv.id];
      try { const ids = await getSvIdsByUser(user.id); if (ids.length) allSvIds = ids.includes(sv.id) ? ids : [sv.id, ...ids]; } catch (e) {}
      setSvIds(allSvIds);

      const { routes: r, statuses: s } = await getStatusesBySv(p.id, allSvIds);
      rememberRoutes(r);
      setRoutes(r);
      setStatuses(s);

      const vacs = await getVacationsBySv(p.id, allSvIds);
      setVacations(vacs);

      // Service%
      const routeIds = r.map(rt => rt.id);
      if (routeIds.length) {
        const svcData = await getRowsByRouteChunks('service_pct', 'route_id, week_number, service_pct', p.id, routeIds);
        const svcMap = {};
        (svcData || []).forEach(s => {
          if (!svcMap[s.route_id]) svcMap[s.route_id] = {};
          svcMap[s.route_id][s.week_number] = s.service_pct;
        });
        setServicePct(svcMap);

        const { getVisitFactsByRoutes } = await import('../lib/data');
        const facts = await getVisitFactsByRoutes(p.id, routeIds);
        setVisitFacts(facts);

        // ── Данные для нового «Табеля»: Заказ / Прогноз(статус+визиты) / Факт BI ──
        const ordersMap = await getOrdersByRoutes(p.id, routeIds);
        setOrders(ordersMap);

        const sv2 = await getRowsByRouteChunks('daily_statuses', 'route_id, work_date, status_code, plan_visits', p.id, routeIds);
        const svMap = {};
        (sv2 || []).forEach(s => {
          if (!svMap[s.route_id]) svMap[s.route_id] = {};
          svMap[s.route_id][s.work_date] = { code: s.status_code, plan_visits: s.plan_visits };
        });
        setStatusesV(svMap);

        const fMap = {};
        facts.forEach(f => {
          if (!fMap[f.route_id]) fMap[f.route_id] = {};
          fMap[f.route_id][f.work_date] = f.visits_fact;
        });
        setFactsMap(fMap);

        // Гиппера
        try {
          const hyper = await getHyperTabellBySv(p.id, allSvIds);
          setHyperData(hyper);
        } catch (e) { console.error('hyper load', e); }
      }
    } catch (err) {
      console.error(err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  function showBanner(msg) {
    setSaveBanner({ show: true, msg });
    setTimeout(() => setSaveBanner({ show: false, msg: '' }), 2200);
  }

  async function handleFillVacancy() {
    if (!vacancyFill || !vacancyName.trim()) return;
    setVacancySaving(true);
    try {
      const { closeVacancyByRoute } = await import('../lib/data');
      await closeVacancyByRoute(vacancyFill.routeId, vacancyName.trim());
      setRoutes(prev => prev.map(r => r.id === vacancyFill.routeId
        ? { ...r, is_vacancy: false, merchandisers: { full_name: vacancyName.trim() } }
        : r));
      setVacancyFill(null);
      setVacancyName('');
      showBanner('✓ Вакансия заполнена');
    } catch (err) {
      showBanner('⚠ Ошибка: ' + err.message);
    } finally {
      setVacancySaving(false);
    }
  }

  // ── Обработчики нового «Табеля» ──────────────────────────────────────────────
  async function handleSaveOrder({ routeId, date, visits }) {
    try {
      const rq = await setOrderVisits({ periodId: period.id, routeId, date, visits });
      setOrders(prev => {
        const cur = prev[routeId]?.[date] || { file_visits: visits, visits };
        return { ...prev, [routeId]: { ...(prev[routeId] || {}), [date]: { ...cur, visits } } };
      });
      showBanner(rq && rq.queued ? '⚠ Нет связи: заказ отправим автоматически' : '✓ Заказ сохранён');
    } catch (err) {
      console.error('[order save]', err);
      showBanner('⚠ Ошибка сохранения заказа: ' + (err?.message || err?.code || 'неизвестно'));
    }
  }

  async function handleSavePrognoz({ routeId, date, code, planVisits }) {
    try {
      let rq;
      if (code === null) {
        rq = await clearStatus({ periodId: period.id, routeId, date });
        setStatusesV(prev => {
          const next = { ...prev };
          if (next[routeId]) { const r = { ...next[routeId] }; delete r[date]; next[routeId] = r; }
          return next;
        });
      } else {
        rq = await setStatus({ periodId: period.id, routeId, date, statusCode: code, planVisits, userId: user.id });
        setStatusesV(prev => ({
          ...prev,
          [routeId]: { ...(prev[routeId] || {}), [date]: { code, plan_visits: planVisits } },
        }));
      }
      showBanner(rq && rq.queued ? '⚠ Нет связи: прогноз отправим автоматически' : (code ? '✓ Прогноз сохранён' : '✕ Прогноз удалён'));
    } catch (err) {
      console.error('[prognoz save]', err);
      showBanner('⚠ Ошибка сохранения прогноза: ' + (err?.message || err?.code || 'неизвестно'));
    }
  }

  const weeks = period ? getWeeksForPeriod(period) : {};
  const weekNums = Object.keys(weeks).map(Number).sort((a,b) => a-b);

  // Текущая неделя (по Москве) — для кнопки игры: открываем, когда прогноз на неё заполнен.
  const todayStr = mskToday();
  const curWeek = weekNums.find(w => weeks[w] && weeks[w].date_from <= todayStr && todayStr <= weeks[w].date_to) ?? null;
  const gameCheck = curWeek != null
    ? checkWeekForecast({ routes, orders, statusesV, hyperData, weekDays: weeks[curWeek].days })
    : { ok: true, routeGaps: [], hyperGaps: [], routeDays: 0, hyperDays: 0 };
  const goToCurWeek = (tabIdx) => {
    const i = weekNums.indexOf(curWeek);
    if (i >= 0) setWIdx(i);
    setTabMode('week');
    setTab(tabIdx);
  };

  // Мягкое напоминание про гиппера: со вторника, пока на текущей неделе есть точки с заказом без плана.
  // Метка на вкладке остаётся, строка наверху скрывается крестиком до следующего дня.
  const hyperGapN = gameCheck.hyperGaps ? gameCheck.hyperGaps.length : 0;
  const isMonday = new Date(todayStr + 'T00:00:00').getDay() === 1;
  const hyperNudgeOn = hyperGapN > 0 && !isMonday;
  const [nudgeHiddenDay, setNudgeHiddenDay] = useState(() => { try { return localStorage.getItem('hyperNudgeHidden') || ''; } catch (e) { return ''; } });
  const hideNudge = () => { try { localStorage.setItem('hyperNudgeHidden', todayStr); } catch (e) {} setNudgeHiddenDay(todayStr); };

  if (loading) return <Loader text="Загружаем табель..." />;

  if (loadError && !svId) return (
    <div style={{ padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontWeight: 600 }}>Не удалось загрузить данные</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>Временный сбой связи с сервером. Нажмите «Повторить».</div>
      <Btn style={{ marginTop: 16 }} onClick={loadData}>Повторить</Btn>
      <Btn color="ghost" style={{ marginTop: 8 }} onClick={logout}>Выйти</Btn>
    </div>
  );

  if (!svId) return (
    <div style={{ padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontWeight: 600 }}>Аккаунт не привязан к СВ</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>Обратитесь к администратору</div>
      <Btn color="ghost" style={{ marginTop: 16 }} onClick={logout}>Выйти</Btn>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Topbar
        title={user.full_name}
        subtitle="Супервайзер"
        rightContent={
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => {
              exportTabellToExcel({ routes, statuses, year: period?.year || 2026, month: period?.month || 9, sheetName: user.full_name, fileName: `табель_${user.full_name}` });
            }} style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: 'white', padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
              📥 Excel
            </button>
            <button onClick={logout} style={{ background: 'rgba(255,255,255,.12)', border: 'none', color: 'white', padding: '6px 12px', borderRadius: 6, fontSize: 12 }}>
              Выйти
            </button>
          </div>
        }
      />

      <NavTabs tabs={['Табель', hyperNudgeOn ? `Гиппера · ${hyperGapN}` : 'Гиппера', 'Отпуска', 'Итоги', 'Моё планирование', 'Приведи друга', { label: 'Мои задачи', badge: taskN }]} active={tab} onChange={setTab} />

      <div style={{ padding: 16, maxWidth: 700, margin: '0 auto', width: '100%' }}>
        {tab !== 6 && <TaskBanners userId={user.id} refreshKey={taskN} onOpen={() => setTab(6)} />}
        {tab === 6 && <TasksTab user={user} onChanged={refreshTaskN} />}

        {/* ── ИГРА «Ловим долю»: открывается, когда прогноз на текущую неделю заполнен ── */}
        <GameBar settings={game} check={gameCheck} weekNo={curWeek}
          getMrcName={(r) => { const m = getMrc(r); return (r.is_vacancy || !m?.full_name) ? `Вакансия` : m.full_name; }}
          onGoRoutes={() => goToCurWeek(0)} onGoHyper={() => goToCurWeek(1)} />

        {/* ── ТАБЕЛЬ (Заказ / Прогноз / Факт BI) ── ПМ сюда не показываем */}
        {tab === 0 && hyperNudgeOn && nudgeHiddenDay !== todayStr && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FFF9E6', border: '1px solid #F0DDA0', borderRadius: 10, padding: '9px 12px', marginBottom: 12, fontSize: 13, color: '#7A5A00' }}>
            <span style={{ flex: 1 }}>Гиппера: не спланировано {hyperGapN} {hyperGapN === 1 ? 'точка' : hyperGapN < 5 ? 'точки' : 'точек'} на эту неделю</span>
            <button onClick={() => goToCurWeek(1)} style={{ border: 'none', background: '#D69E2E', color: '#fff', fontWeight: 700, fontSize: 12, padding: '6px 12px', borderRadius: 7, cursor: 'pointer' }}>Перейти</button>
            <button onClick={hideNudge} aria-label="Скрыть до завтра" style={{ border: 'none', background: 'none', color: '#A08020', fontSize: 18, cursor: 'pointer', padding: '0 4px' }}>×</button>
          </div>
        )}
        {tab === 0 && (
          <TabellTab
            routes={routes.filter(r => !r.is_pm)}
            orders={orders}
            statusesV={statusesV}
            factsMap={factsMap}
            weeks={weeks}
            weekNums={weekNums}
            period={period}
            wIdx={wIdx} setWIdx={setWIdx} mode={tabMode} setMode={setTabMode}
            onSaveOrder={handleSaveOrder}
            onSavePrognoz={handleSavePrognoz}
            onFillVacancy={(route) => {
              const mrc = getMrc(route);
              const isVacant = route.is_vacancy || !mrc?.full_name;
              setVacancyFill({ routeId: route.id, code: route.internal_code });
              setVacancyName(isVacant ? '' : (mrc?.full_name || ''));
            }}
          />
        )}
        {tab === 0 && <PmBlock routes={routes.filter(r => r.is_pm)} onEdit={(route) => {
          const mrc = getMrc(route);
          const isVacant = route.is_vacancy || !mrc?.full_name || /вакансия/i.test(mrc.full_name);
          setVacancyFill({ routeId: route.id, code: route.internal_code });
          setVacancyName(isVacant ? '' : (mrc?.full_name || ''));
        }} />}

        {/* ── ОТПУСКА ── */}
        {tab === 2 && <VacationTab routes={routes} vacations={vacations} period={period} userId={user.id} onUpdate={setVacations} />}

        {/* ── ИТОГИ ── */}
        {tab === 3 && <SummaryTab routes={routes.filter(r => !r.is_pm)} statuses={statuses} orders={orders} statusesV={statusesV} factsMap={factsMap} weeks={weeks} weekNums={weekNums} servicePct={servicePct} />}

        {tab === 5 && <ReferralBoard mode="sv" userId={user.id} />}
        {tab === 4 && <SvPlanner svId={svId} svIds={svIds} userId={user.id} />}

        {/* ── ГИППЕРА ── */}
        {tab === 1 && <HyperTab hyperData={hyperData} weeks={weeks} weekNums={weekNums}
          wIdx={wIdx} setWIdx={setWIdx} mode={tabMode} setMode={setTabMode}
          onSavePlan={async ({ pointIds, primaryId, date, visits, code }) => {
            try {
              // #2а/#7 — точка сведена: статус+план пишем на основную точку, остальные обнуляем, чтобы сумма = введённому
              await setHyperPlan({ periodId: period.id, hyperPointId: primaryId, date, visits, code });
              const others = (pointIds || [primaryId]).filter(id => id !== primaryId);
              for (const id of others) await setHyperPlan({ periodId: period.id, hyperPointId: id, date, visits: 0, code: null });
              setHyperData(prev => {
                const daily = { ...prev.daily };
                const set = (id, val, cd) => { const cur = daily[id]?.[date] || {}; daily[id] = { ...(daily[id] || {}), [date]: { ...cur, plan: val, code: cd } }; };
                set(primaryId, visits, code ?? null);
                others.forEach(id => set(id, 0, null));
                return { ...prev, daily };
              });
              showBanner('✓ Планирование сохранено');
            } catch (e) { showBanner('⚠ Ошибка сохранения'); }
          }} />}
      </div>
      <Modal open={!!vacancyFill} onClose={() => { setVacancyFill(null); setVacancyName(''); }} title={`ФИО МРЧ · ${vacancyFill?.code || ''}`}>
        <div style={{ padding: '0 20px 20px' }}>
          <Input label="ФИО нового МРЧ" value={vacancyName} onChange={setVacancyName} placeholder="Иванова Мария Петровна" />
          <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: -8, marginBottom: 14 }}>
            Официальные данные подтвердятся при следующей загрузке SUPERVISOR
          </div>
          <Btn size="full" onClick={handleFillVacancy} disabled={!vacancyName.trim() || vacancySaving}>
            {vacancySaving ? 'Сохранение...' : 'Сохранить'}
          </Btn>
        </div>
      </Modal>
      <SaveBanner show={saveBanner.show} message={saveBanner.msg} />
    </div>
  );
}

// ── ВКЛАДКА ТАБЕЛЬ: Заказ / Прогноз / Факт BI ────────────────────────────────
const SL_TARGET = 95; // порог service level, %

// Плановые визиты дня по прогнозу: null — прогноз не проставлен; 0 — «−»-статус;
// для «+» и выхода — сохранённые визиты, иначе заказ дня.
function planVisitsOf(st, orderVisits) {
  if (!st || !st.code) return null;
  const sign = statusSign(st.code);
  if (sign === -1) return 0;
  if (sign === 0) return null;            // ПЛ ВЫХ — нейтрально
  return st.plan_visits != null ? st.plan_visits : (orderVisits != null ? orderVisits : 0);
}

// #6 — панель «Итог по дням»: суммы Заказ/Прогноз/Факт и SL% по каждому дню недели + колонка «неделя целиком».
function DaySumPanel({ routes, orders, statusesV, factsMap, weekDays, weekNo }) {
  const perDay = weekDays.map(({ day, dow, date }) => {
    let order = 0, plan = 0, fact = 0; let hasFact = false;
    routes.forEach(r => {
      const ordV = orders[r.id]?.[date]?.visits ?? 0;
      order += ordV;
      const pv = planVisitsOf(statusesV[r.id]?.[date], ordV);
      if (pv != null) plan += pv;
      const f = factsMap[r.id]?.[date];
      if (f != null) { fact += f; hasFact = true; }
    });
    const sl = order > 0 && hasFact ? Math.round(fact / order * 100) : null;
    return { day, dow, date, order, plan, fact, hasFact, sl };
  });
  const wk = perDay.reduce((a, d) => {
    a.order += d.order; a.plan += d.plan; a.fact += d.fact; if (d.hasFact) a.hasFact = true; return a;
  }, { order: 0, plan: 0, fact: 0, hasFact: false });
  wk.sl = wk.order > 0 && wk.hasFact ? Math.round(wk.fact / wk.order * 100) : null;

  const colW = 40;
  const rowLabel = { width: 62, fontSize: 11, color: 'var(--text-muted)', textAlign: 'left', paddingLeft: 6 };
  const cellS = (weekend) => ({ width: colW, textAlign: 'center', fontSize: 12, fontVariantNumeric: 'tabular-nums', opacity: weekend ? 0.5 : 1 });
  const slColor = (sl) => sl == null ? 'var(--text-light)' : sl >= SL_TARGET ? 'var(--green)' : 'var(--red)';

  return (
    <Card style={{ marginBottom: 12, overflowX: 'auto' }}>
      <div style={{ padding: '9px 12px 4px', fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
        Итог по дням · W{weekNo}
      </div>
      <div style={{ padding: '0 8px 10px', minWidth: 62 + colW * (weekDays.length + 1) }}>
        {/* шапка дней */}
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <div style={rowLabel}></div>
          {perDay.map(d => (
            <div key={d.date} style={{ ...cellS(isWeekend(d.dow)), fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.15 }}>
              {DOW_RU[d.dow]}<br/>{d.day}
            </div>
          ))}
          <div style={{ width: colW, textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--accent-dark,#1F3864)' }}>нед.</div>
        </div>
        {/* строки */}
        {[
          { k: 'Заказ',   get: (d) => d.order, tot: wk.order, bold: false },
          { k: 'Прогноз', get: (d) => d.plan,  tot: wk.plan,  bold: true },
          { k: 'Факт',    get: (d) => d.hasFact ? d.fact : '—', tot: wk.hasFact ? wk.fact : '—', bold: false, green: true },
        ].map(row => (
          <div key={row.k} style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
            <div style={rowLabel}>{row.k}</div>
            {perDay.map(d => (
              <div key={d.date} style={{ ...cellS(isWeekend(d.dow)), fontWeight: row.bold ? 700 : 400, color: row.green ? 'var(--green)' : 'var(--text)' }}>{row.get(d)}</div>
            ))}
            <div style={{ width: colW, textAlign: 'center', fontSize: 12.5, fontWeight: 700, color: row.green ? 'var(--green)' : 'var(--accent-dark,#1F3864)' }}>{row.tot}</div>
          </div>
        ))}
        {/* SL% по дню */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '3px 0 0', marginTop: 2, borderTop: '1px solid var(--border)' }}>
          <div style={rowLabel}>SL %</div>
          {perDay.map(d => (
            <div key={d.date} style={{ ...cellS(isWeekend(d.dow)), fontSize: 11, fontWeight: 700, color: slColor(d.sl) }}>{d.sl == null ? '—' : d.sl}</div>
          ))}
          <div style={{ width: colW, textAlign: 'center', fontSize: 12, fontWeight: 800, color: slColor(wk.sl) }}>{wk.sl == null ? '—' : wk.sl + '%'}</div>
        </div>
      </div>
    </Card>
  );
}

// #1 — накопительная панель «Итог по дням» для гипперов (аналог DaySumPanel).
function HyperDaySumPanel({ stores, daily, weekDays, weekNo }) {
  const perDay = weekDays.map(({ day, dow, date }) => {
    let order = 0, plan = 0, fact = 0, hasFact = false;
    stores.forEach(st => st.pointIds.forEach(id => {
      const c = daily[id]?.[date]; if (!c) return;
      order += c.order || 0;
      if (c.plan != null) plan += c.plan;
      if (c.fact != null) { fact += c.fact; hasFact = true; }
    }));
    const sl = order > 0 && hasFact ? Math.round(fact / order * 100) : null;
    return { day, dow, date, order, plan, fact, hasFact, sl };
  });
  const wk = perDay.reduce((a, d) => { a.order += d.order; a.plan += d.plan; a.fact += d.fact; if (d.hasFact) a.hasFact = true; return a; }, { order: 0, plan: 0, fact: 0, hasFact: false });
  wk.sl = wk.order > 0 && wk.hasFact ? Math.round(wk.fact / wk.order * 100) : null;
  const colW = 40;
  const rowLabel = { width: 62, fontSize: 11, color: 'var(--text-muted)', textAlign: 'left', paddingLeft: 6 };
  const cellS = (weekend) => ({ width: colW, textAlign: 'center', fontSize: 12, fontVariantNumeric: 'tabular-nums', opacity: weekend ? 0.5 : 1 });
  const slColor = (sl) => sl == null ? 'var(--text-light)' : sl >= SL_TARGET ? 'var(--green)' : 'var(--red)';
  return (
    <Card style={{ marginBottom: 12, overflowX: 'auto' }}>
      <div style={{ padding: '9px 12px 4px', fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Итог по гипперам · W{weekNo}</div>
      <div style={{ padding: '0 8px 10px', minWidth: 62 + colW * (weekDays.length + 1) }}>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <div style={rowLabel}></div>
          {perDay.map(d => <div key={d.date} style={{ ...cellS(isWeekend(d.dow)), fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.15 }}>{DOW_RU[d.dow]}<br/>{d.day}</div>)}
          <div style={{ width: colW, textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--accent-dark,#1F3864)' }}>нед.</div>
        </div>
        {[
          { k: 'Заказ',   get: (d) => d.order, tot: wk.order, bold: false },
          { k: 'Планир.', get: (d) => d.plan,  tot: wk.plan,  bold: true },
          { k: 'Факт',    get: (d) => d.hasFact ? d.fact : '—', tot: wk.hasFact ? wk.fact : '—', bold: false, green: true },
        ].map(row => (
          <div key={row.k} style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
            <div style={rowLabel}>{row.k}</div>
            {perDay.map(d => <div key={d.date} style={{ ...cellS(isWeekend(d.dow)), fontWeight: row.bold ? 700 : 400, color: row.green ? 'var(--green)' : 'var(--text)' }}>{row.get(d)}</div>)}
            <div style={{ width: colW, textAlign: 'center', fontSize: 12.5, fontWeight: 700, color: row.green ? 'var(--green)' : 'var(--accent-dark,#1F3864)' }}>{row.tot}</div>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', padding: '3px 0 0', marginTop: 2, borderTop: '1px solid var(--border)' }}>
          <div style={rowLabel}>SL %</div>
          {perDay.map(d => <div key={d.date} style={{ ...cellS(isWeekend(d.dow)), fontSize: 11, fontWeight: 700, color: slColor(d.sl) }}>{d.sl == null ? '—' : d.sl}</div>)}
          <div style={{ width: colW, textAlign: 'center', fontSize: 12, fontWeight: 800, color: slColor(wk.sl) }}>{wk.sl == null ? '—' : wk.sl + '%'}</div>
        </div>
      </div>
    </Card>
  );
}

function TabellTab({ routes, orders, statusesV, factsMap, weeks, weekNums, period, wIdx, setWIdx, mode, setMode, onSaveOrder, onSavePrognoz, onFillVacancy }) {
  const [orderEdit, setOrderEdit] = useState(null);     // { routeId, date, base, cur, mrc }
  const [prognozEdit, setPrognozEdit] = useState(null); // { routeId, date, order, cur, mrc }
  const [mDetail, setMDetail] = useState(null);

  const w = weekNums[wIdx];
  const weekDays = weeks[w]?.days || [];
  const mrcOf = (route) => (route.is_vacancy || !getMrc(route)?.full_name) ? `Вакансия (${route.internal_code})` : getMrc(route).full_name;

  // ── НЕДЕЛЯ: подсчёт свода и итогов ──
  const attentions = [];
  let tOrder = 0, tOrderBase = 0, tPlan = 0;

  routes.forEach(route => {
    weekDays.forEach(({ date }) => {
      const ord = orders[route.id]?.[date];
      const ordV = ord?.visits ?? 0;
      const baseV = ord?.file_visits ?? 0;
      tOrder += ordV; tOrderBase += baseV;
      const st = statusesV[route.id]?.[date];
      const pv = planVisitsOf(st, ordV);
      if (pv != null) tPlan += pv;
    });
  });
  // Заказ по команде за неделю просел относительно файла?
  if (tOrder < tOrderBase) {
    attentions.push({ who: 'Команда за неделю', why: `суммарный заказ уменьшился: было ${tOrderBase}, стало ${tOrder} (−${tOrderBase - tOrder})`, kind: 'order' });
  }

  function cellOrder(route, date) {
    const ord = orders[route.id]?.[date];
    const v = ord?.visits ?? 0;
    const base = ord?.file_visits ?? 0;
    const prev = ord?.prev_visits;          // #7 — заказ, перезаписанный из факта
    const moved = v !== base;
    return (
      <div onClick={() => setOrderEdit({ routeId: route.id, date, base, cur: v, mrc: mrcOf(route) })}
        style={{ ...cellBox, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' }}>
        <span>{v || '·'}</span>
        {/* #7 — прежнее значение заказа (до перезаписи фактом) мелко сверху */}
        {prev != null && prev !== v && <span title={`было ${prev} (из начала месяца), перезаписано фактом`} style={{ position: 'absolute', top: -1, right: 2, fontSize: 7.5, fontWeight: 700, color: 'var(--text-light)', textDecoration: 'line-through' }}>{prev}</span>}
        {prev == null && moved && <span style={{ position: 'absolute', top: 0, right: 2, fontSize: 7, fontWeight: 700, color: v > base ? 'var(--green)' : '#A8720C' }}>{v > base ? '+' : ''}{v - base}</span>}
      </div>
    );
  }
  function cellPrognoz(route, date, mrcName) {
    const ord = orders[route.id]?.[date];
    const ordV = ord?.visits ?? 0;
    const st = statusesV[route.id]?.[date];
    const pv = planVisitsOf(st, ordV);
    const sign = st ? statusSign(st.code) : null;
    // рамка: заказ>0 и (−статус ИЛИ прогноз≠заказ ИЛИ ПЛ ВЫХ при заказе)
    let frame = false;
    if (ordV > 0 && st && st.code) {
      if (sign === -1) frame = true;
      else if (sign === 0) frame = true;
      else if (pv !== ordV) frame = true;
    }
    if (frame) attentions.push({ who: `${mrcName} · ${parseInt(date.slice(8),10)} ${moShort(date)}`, why: sign === -1 ? `${st.code} не перекрыт (0 виз.)` : `прогноз ${pv ?? '—'} ≠ заказ ${ordV}`, kind: 'plan' });
    const sc = st?.code ? getStatusColor(st.code) : null;
    const showVal = !st ? '' : (sign === 0 ? '–' : (sign === -1 ? 0 : pv));
    return (
      <div onClick={() => setPrognozEdit({ routeId: route.id, date, order: ordV, cur: st || null, mrc: mrcName })}
        style={{ ...cellBox, cursor: 'pointer',
          background: frame ? 'var(--red-bg)' : sc ? sc.bg : 'var(--surface2)',
          color: frame ? 'var(--red)' : sc ? sc.fg : 'var(--text-muted)',
          boxShadow: frame ? 'inset 0 0 0 2px var(--red)' : 'none' }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>{showVal}</span>
        {st?.code && st.code !== '1' && <span style={{ fontSize: 7, lineHeight: 1 }}>{statusShort(st.code)}</span>}
      </div>
    );
  }
  function cellFact(route, date, mrcName) {
    const f = factsMap[route.id]?.[date];
    const ord = orders[route.id]?.[date];
    const st = statusesV[route.id]?.[date];
    const pv = planVisitsOf(st, ord?.visits ?? 0);
    let frame = false;
    if (f != null && st && st.code && pv != null && f !== pv) frame = true;
    if (frame) attentions.push({ who: `${mrcName} · ${parseInt(date.slice(8),10)} ${moShort(date)}`, why: `факт BI ${f} ≠ прогноз ${pv}`, kind: 'fact' });
    return (
      <div style={{ ...cellBox, color: 'var(--text-muted)',
        boxShadow: frame ? 'inset 0 0 0 2px var(--red)' : 'none', ...(frame ? { color: 'var(--red)' } : {}) }}>
        <span>{f == null ? '—' : f}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Переключатель периода + навигация */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {['week','month'].map(m => (
            <button key={m} onClick={() => setMode(m)} style={{ border: 0, background: mode === m ? 'var(--accent)' : 'white', color: mode === m ? 'white' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, padding: '7px 14px', cursor: 'pointer' }}>
              {m === 'week' ? 'Неделя' : 'Месяц целиком'}
            </button>
          ))}
        </div>
        {mode === 'week' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setWIdx(i => Math.max(0, i-1))} disabled={wIdx===0} style={navBtn}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 13, minWidth: 60, textAlign: 'center' }}>W{w}</span>
            <button onClick={() => setWIdx(i => Math.min(weekNums.length-1, i+1))} disabled={wIdx===weekNums.length-1} style={navBtn}>›</button>
          </div>
        )}
      </div>

      {mode === 'week' ? (
        <>
          {/* #6 — Итог по дням (Заказ/Прогноз/Факт/SL% по каждому дню + неделя целиком) */}
          <DaySumPanel routes={routes} orders={orders} statusesV={statusesV} factsMap={factsMap} weekDays={weekDays} weekNo={w} />

          {/* заголовок дней */}
          <div style={{ display: 'flex', gap: 2, paddingLeft: 58, marginBottom: 4 }}>
            {weekDays.map(({ day, dow }) => (
              <div key={day} style={{ width: 34, textAlign: 'center', fontSize: 10, color: isWeekend(dow) ? 'var(--text-light)' : 'var(--text-muted)' }}>
                {DOW_RU[dow].slice(0,2).toUpperCase()}<br/>{day}
              </div>
            ))}
            <div style={{ width: 34, textAlign: 'center', fontSize: 10, color: 'var(--text-muted)' }}>Σ</div>
          </div>

          {routes.map(route => {
            const mrcName = mrcOf(route);
            let so = 0, sp = 0, sf = 0;
            weekDays.forEach(({ date }) => {
              const ord = orders[route.id]?.[date];
              so += ord?.visits ?? 0;
              const st = statusesV[route.id]?.[date];
              const pv = planVisitsOf(st, ord?.visits ?? 0);
              if (pv != null) sp += pv;
              const f = factsMap[route.id]?.[date];
              if (f != null) sf += f;
            });
            return (
              <Card key={route.id} style={{ marginBottom: 8 }}>
                <div style={{ padding: '8px 12px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {mrcName}
                    <button onClick={() => onFillVacancy(route)} title={route.is_vacancy ? 'Заполнить вакансию' : 'Сменить МРЧ'}
                      style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: 11, color: route.is_vacancy ? 'var(--accent)' : 'var(--text-light)' }}>
                      {route.is_vacancy ? '👤 заполнить' : '✏️'}
                    </button>
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--text-light)', textAlign: 'right' }}>
                    {route.internal_code}
                    <RecruitChip routeId={route.id} style={{ display: 'flex', marginTop: 2 }} />
                    {route.external_code && <><br/><span style={{ fontSize: 9, color: 'var(--text-light)', wordBreak: 'break-all' }}>{route.external_code}</span></>}
                  </span>
                </div>
                {[
                  { label: 'Заказ',    render: (d) => cellOrder(route, d), sum: so },
                  { label: 'Прогноз',  render: (d) => cellPrognoz(route, d, mrcName), sum: sp, bold: true },
                  { label: 'Факт BI',  render: (d) => cellFact(route, d, mrcName), sum: sf },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '0 4px 2px' }}>
                    <div style={{ width: 54, fontSize: 11, color: 'var(--text-muted)', paddingLeft: 4 }}>{row.label}</div>
                    {weekDays.map(({ date }) => <div key={date} style={{ width: 34 }}>{row.render(date)}</div>)}
                    <div style={{ width: 34, textAlign: 'center', fontSize: 12, fontWeight: row.bold ? 700 : 400, color: 'var(--text-muted)' }}>{row.sum}</div>
                  </div>
                ))}
              </Card>
            );
          })}

          {/* Δ по команде */}
          <div style={{ display: 'flex', gap: 8, margin: '6px 0 14px', flexWrap: 'wrap' }}>
            <span style={chip}>Заказ <b>{tOrder}</b>{tOrder < tOrderBase ? <span style={{ color: 'var(--red)' }}> / {tOrderBase} ↓</span> : null}</span>
            <span style={chip}>Прогноз <b>{tPlan}</b></span>
            <span style={{ ...chip, background: tPlan === tOrder ? 'var(--green-bg,#E4F2EA)' : 'var(--red-bg)', color: tPlan === tOrder ? 'var(--green)' : 'var(--red)' }}>Δ {tPlan - tOrder > 0 ? '+' : ''}{tPlan - tOrder}</span>
          </div>

          {/* #4 — подсказки-алёрты у СВ убраны; сигналы теперь только накопительно у ТМ */}
        </>
      ) : (
        <MonthView routes={routes} orders={orders} factsMap={factsMap} weeks={weeks} weekNums={weekNums} mrcOf={mrcOf} mDetail={mDetail} setMDetail={setMDetail} />
      )}

      {/* ── Пикер заказа ── */}
      <Modal open={!!orderEdit} onClose={() => setOrderEdit(null)} title={`Заказ · ${orderEdit?.mrc || ''}`}>
        {orderEdit && <OrderEditor edit={orderEdit} onClose={() => setOrderEdit(null)}
          onSave={(visits) => { onSaveOrder({ routeId: orderEdit.routeId, date: orderEdit.date, visits }); setOrderEdit(null); }} />}
      </Modal>

      {/* ── Пикер прогноза ── */}
      <Modal open={!!prognozEdit} onClose={() => setPrognozEdit(null)} title={`Прогноз · ${prognozEdit?.mrc || ''}`}>
        {prognozEdit && <PrognozEditor edit={prognozEdit} onClose={() => setPrognozEdit(null)}
          onSave={(code, planVisits) => { onSavePrognoz({ routeId: prognozEdit.routeId, date: prognozEdit.date, code, planVisits }); setPrognozEdit(null); }} />}
      </Modal>
    </div>
  );
}

const cellBox = { position: 'relative', height: 32, borderRadius: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontVariantNumeric: 'tabular-nums', lineHeight: 1 };
const navBtn = { width: 30, height: 30, borderRadius: 6, border: '1px solid var(--border)', background: 'white', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 15 };
const chip = { fontSize: 12.5, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', color: 'var(--text-muted)' };

function OrderEditor({ edit, onClose, onSave }) {
  const [v, setV] = useState(String(edit.cur));
  return (
    <div style={{ padding: '0 20px 20px' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        {parseInt(edit.date.slice(8),10)} {moShort(edit.date)}. Из файла на день: <b>{edit.base}</b>. СВ переносит заказ между днями/маршрутами — если суммарный заказ по неделе станет меньше файла, уйдёт сигнал ТМ.
      </div>
      <Input label="Заказ на день (визитов)" value={v} onChange={setV} placeholder="0" />
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <Btn size="md" color="ghost" onClick={onClose} style={{ flex: 1 }}>Отмена</Btn>
        <Btn size="md" onClick={() => onSave(Math.max(0, parseInt(v || '0', 10)))} style={{ flex: 1 }}>Сохранить</Btn>
      </div>
    </div>
  );
}

function PrognozEditor({ edit, onClose, onSave }) {
  const [code, setCode] = useState(edit.cur?.code || null);
  const [vis, setVis] = useState(String(edit.cur ? (planVisitsOf(edit.cur, edit.order) ?? edit.order) : edit.order));
  const sign = code ? statusSign(code) : null;
  const visDisabled = sign === -1 || sign === 0;
  return (
    <div style={{ padding: '0 16px 16px', maxHeight: '72vh', overflowY: 'auto' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 10px' }}>
        {parseInt(edit.date.slice(8),10)} {moShort(edit.date)}. Заказ на день: <b>{edit.order}</b>. «+»-статусы несут визиты, «−» ставят 0.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--surface2)', borderRadius: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Визитов в прогнозе</span>
        <input type="number" min="0" value={visDisabled ? 0 : vis} disabled={visDisabled}
          onChange={e => setVis(e.target.value)}
          style={{ width: 60, padding: '6px 8px', borderRadius: 6, border: '1.5px solid var(--border)', fontSize: 14, opacity: visDisabled ? 0.5 : 1 }} />
        {/* #5 — кнопка ОК сразу рядом с числом; активна только если выбран формат */}
        <button onClick={() => onSave(code, visDisabled ? 0 : Math.max(0, parseInt(vis || '0', 10)))} disabled={!code}
          title={!code ? 'Сначала выберите формат ниже' : 'Сохранить'}
          style={{ padding: '7px 14px', borderRadius: 6, border: 0, background: code ? 'var(--accent)' : 'var(--border)', color: code ? 'white' : 'var(--text-light)', fontWeight: 700, fontSize: 13, cursor: code ? 'pointer' : 'default' }}>
          ОК
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-light)' }}>заказ {edit.order}</span>
      </div>
      {STATUSES.map(group => (
        <div key={group.group}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '8px 0 6px' }}>{group.group}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {group.items.map(item => {
              const c = getStatusColor(item.code);
              const selected = item.code === code;
              return (
                <button key={item.code} onClick={() => {
                  setCode(item.code);
                  const s = statusSign(item.code);
                  if (s === -1 || s === 0) setVis('0');
                  else if (!vis || vis === '0') setVis(String(edit.order));
                }}
                  style={{ padding: '9px 10px', borderRadius: 8, border: selected ? '2px solid var(--accent)' : 'none', background: c.bg, color: c.fg, fontWeight: 700, fontSize: 12, textAlign: 'left', cursor: 'pointer' }}>
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button onClick={() => { onSave(null, null); }} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1.5px solid var(--border)', background: 'white', color: 'var(--text-muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>✕ Очистить</button>
        <Btn size="md" onClick={() => onSave(code, visDisabled ? 0 : Math.max(0, parseInt(vis || '0', 10)))} disabled={!code} style={{ flex: 1 }}>Сохранить</Btn>
      </div>
    </div>
  );
}

function MonthView({ routes, orders, factsMap, weeks, weekNums, mrcOf, mDetail, setMDetail }) {
  let tOrder = 0, tFact = 0;
  const rows = routes.map(route => {
    const weekCells = weekNums.map(w => {
      const days = (weeks[w]?.days || []).filter(d => !isWeekend(d.dow));
      return days.map(({ day, dow, date }) => {
        const ordV = orders[route.id]?.[date]?.visits ?? 0;
        const f = factsMap[route.id]?.[date];
        tOrder += ordV; if (f != null) tFact += f;
        const pct = ordV > 0 && f != null ? Math.round(f / ordV * 100) : (ordV > 0 ? null : undefined);
        return { day, dow, date, ordV, f, pct };
      });
    });
    return { route, weekCells };
  });
  const slAll = tOrder > 0 ? Math.round(tFact / tOrder * 100) : 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, margin: '2px 0 12px', flexWrap: 'wrap' }}>
        <span style={chip}>Заказ <b>{tOrder}</b></span>
        <span style={chip}>Факт BI <b>{tFact}</b></span>
        <span style={{ ...chip, background: slAll >= SL_TARGET ? 'var(--green-bg,#E4F2EA)' : 'var(--red-bg)', color: slAll >= SL_TARGET ? 'var(--green)' : 'var(--red)' }}>Service level {slAll}%</span>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
        <span>зелёный — SL ≥ {SL_TARGET}%</span><span>красный — ниже</span><span>серый — заказа нет</span>
      </div>
      {rows.map(({ route, weekCells }) => (
        <Card key={route.id} style={{ marginBottom: 8 }}>
          <div style={{ padding: '8px 12px 4px', fontSize: 13, fontWeight: 600 }}>{mrcOf(route)}
            <span style={{ fontSize: 10, color: 'var(--text-light)', fontWeight: 400, marginLeft: 8 }}>{route.internal_code}</span>
            <RecruitChip routeId={route.id} style={{ marginLeft: 8, verticalAlign: 'middle' }} />
            {route.external_code && <span style={{ fontSize: 9, color: 'var(--text-light)', fontWeight: 400, marginLeft: 6, wordBreak: 'break-all' }}>· {route.external_code}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 12px 10px' }}>
            {weekCells.map((cells, wi) => (
              <div key={wi} style={{ display: 'flex', gap: 2 }}>
                {cells.map(cell => {
                  const bg = cell.pct === undefined ? 'var(--surface2)' : cell.pct == null ? '#EDEDED' : cell.pct >= SL_TARGET ? '#E4F2EA' : 'var(--red-bg)';
                  const fg = cell.pct === undefined ? 'var(--text-light)' : cell.pct == null ? '#888' : cell.pct >= SL_TARGET ? 'var(--green)' : 'var(--red)';
                  return (
                    <div key={cell.date} onClick={() => setMDetail({ mrc: mrcOf(route), ...cell })}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
                      <div style={{ fontSize: 8, color: 'var(--text-light)', lineHeight: 1.15, marginBottom: 2, textAlign: 'center' }}>{DOW_RU[cell.dow]}<br/>{cell.day}</div>
                      <div style={{ width: 26, height: 28, borderRadius: 5, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>
                        {cell.pct === undefined ? '·' : cell.pct == null ? '–' : cell.pct}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      ))}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
        {mDetail ? (
          <span><b style={{ color: 'var(--text)' }}>{mDetail.mrc} · {parseInt(mDetail.date.slice(8),10)} {moShort(mDetail.date)}</b>
            <span style={{ marginLeft: 14 }}>Заказ <b>{mDetail.ordV}</b></span>
            <span style={{ marginLeft: 14 }}>Факт BI <b>{mDetail.f == null ? '—' : mDetail.f}</b></span>
            <span style={{ marginLeft: 14 }}>SL <b>{mDetail.pct == null ? '—' : mDetail.pct + '%'}</b></span>
          </span>
        ) : <i>Кликните день — заказ / факт BI / SL%.</i>}
      </div>
    </div>
  );
}

// ── ВКЛАДКА ГИППЕРА: Заказ / Планирование / Факт по точкам (СВ правит План) ───
function HyperTab({ hyperData, weeks, weekNums, wIdx, setWIdx, mode, setMode, onSavePlan }) {
  const [mDetail, setMDetail] = useState(null);
  const [planEdit, setPlanEdit] = useState(null); // {pointId, date, order, cur, client}
  const { points, daily } = hyperData;

  if (!points || !points.length) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
      По вашим маршрутам гипперов нет (или админ ещё не загрузил заказ).
    </div>;
  }

  // #7 — одна физическая точка (ship_to) может быть на нескольких маршрутах → сводим в ОДНУ точку.
  const byShip = {};
  points.forEach(p => {
    const key = String(p.ship_to);
    if (!byShip[key]) byShip[key] = { ship_to: p.ship_to, client: p.client, address: p.address, pointIds: [] };
    byShip[key].pointIds.push(p.id);
    if (p.client && !byShip[key].client) byShip[key].client = p.client;
    if (p.address && !byShip[key].address) byShip[key].address = p.address;
  });
  const stores = Object.values(byShip);
  stores.forEach(g => { g.pointIds.sort(); g.primaryId = g.pointIds[0]; });
  stores.sort((a, b) => String(a.client || a.ship_to).localeCompare(String(b.client || b.ship_to)));

  const w = weekNums[wIdx];
  const weekDays = weeks[w]?.days || [];
  // суммы по группе точек (order/plan/fact лежат по отдельным маршрутным точкам одного ship_to)
  const orderOf = (ids, date) => ids.reduce((s, id) => s + (daily[id]?.[date]?.order || 0), 0);
  const planOf  = (ids, date) => { let any = false, s = 0; ids.forEach(id => { const v = daily[id]?.[date]?.plan; if (v != null) { any = true; s += v; } }); return any ? s : null; };
  const factOf  = (ids, date) => { let any = false, s = 0; ids.forEach(id => { const v = daily[id]?.[date]?.fact; if (v != null) { any = true; s += v; } }); return any ? s : null; };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {['week','month'].map(m => (
            <button key={m} onClick={() => setMode(m)} style={{ border: 0, background: mode === m ? 'var(--accent)' : 'white', color: mode === m ? 'white' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, padding: '7px 14px', cursor: 'pointer' }}>
              {m === 'week' ? 'Неделя' : 'Месяц целиком'}
            </button>
          ))}
        </div>
        {mode === 'week' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setWIdx(i => Math.max(0, i-1))} disabled={wIdx===0} style={navBtn}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 13, minWidth: 60, textAlign: 'center' }}>W{w}</span>
            <button onClick={() => setWIdx(i => Math.min(weekNums.length-1, i+1))} disabled={wIdx===weekNums.length-1} style={navBtn}>›</button>
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
        Гиппера — точки-магазины (по ship_to, сведены в одну точку, даже если на нескольких маршрутах). Заказ из файла, Факт из BI; клик по «Планированию» — поставить визиты. SL = Факт / Заказ.
      </div>

      {mode === 'week' ? (
        <>
          {/* #1 — накопительная панель по гипперам */}
          <HyperDaySumPanel stores={stores} daily={daily} weekDays={weekDays} weekNo={w} />

          <div style={{ display: 'flex', gap: 2, paddingLeft: 62, marginBottom: 4 }}>
            {weekDays.map(({ day, dow }) => (
              <div key={day} style={{ width: 34, textAlign: 'center', fontSize: 10, color: isWeekend(dow) ? 'var(--text-light)' : 'var(--text-muted)' }}>
                {DOW_RU[dow].slice(0,2).toUpperCase()}<br/>{day}
              </div>
            ))}
            <div style={{ width: 34, textAlign: 'center', fontSize: 10, color: 'var(--text-muted)' }}>Σ</div>
          </div>

          {stores.map(store => {
            const ids = store.pointIds;
            let so = 0, sp = 0, sf = 0;
            weekDays.forEach(({ date }) => { so += orderOf(ids, date); const p = planOf(ids, date); if (p != null) sp += p; const f = factOf(ids, date); if (f != null) sf += f; });
            return (
              <Card key={store.ship_to} style={{ marginBottom: 8 }}>
                <div style={{ padding: '8px 8px 8px', borderTop: 'none' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, padding: '2px 4px 2px' }}>{store.client}
                    <span style={{ color: 'var(--text-light)', fontWeight: 400, fontSize: 10 }}> · {store.ship_to}</span>
                    {ids.length > 1 && <span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 10, marginLeft: 6 }}>({ids.length} маршр.)</span>}
                  </div>
                  {store.address && <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-light)', padding: '0 4px 6px' }}>📍 {store.address}</div>}
                  {/* Заказ */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <div style={{ width: 58, fontSize: 11, color: 'var(--text-muted)', paddingLeft: 4 }}>Заказ</div>
                    {weekDays.map(({ date }) => { const o = orderOf(ids, date); return <div key={date} style={{ width: 34 }}><div style={{ ...cellBox, color: 'var(--text-muted)' }}>{o ? o : '·'}</div></div>; })}
                    <div style={{ width: 34, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>{so}</div>
                  </div>
                  {/* Планирование (правит СВ) — #2а: статусы выхода, как в табеле */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <div style={{ width: 58, fontSize: 11, color: 'var(--text)', paddingLeft: 4 }}>Планир.</div>
                    {weekDays.map(({ date }) => {
                      const o = orderOf(ids, date); const p = planOf(ids, date);
                      const code = daily[store.primaryId]?.[date]?.code || null;
                      const sign = code ? statusSign(code) : null;
                      const sc = code ? getStatusColor(code) : null;
                      // рамка: заказ>0 и (−статус / ПЛ ВЫХ / план≠заказ)
                      const frame = o > 0 && code && (sign === -1 || sign === 0 || p !== o);
                      const showVal = !code ? (p == null ? '·' : p) : (sign === 0 ? '–' : (sign === -1 ? 0 : (p ?? o)));
                      return <div key={date} style={{ width: 34 }}>
                        <div onClick={() => setPlanEdit({ pointIds: ids, primaryId: store.primaryId, date, order: o, cur: code ? { code, plan_visits: p } : null, client: store.client })}
                          style={{ ...cellBox, cursor: 'pointer',
                            background: frame ? 'var(--red-bg)' : sc ? sc.bg : 'var(--surface2)',
                            color: frame ? 'var(--red)' : sc ? sc.fg : 'var(--text-primary,#1b1d1a)',
                            boxShadow: frame ? 'inset 0 0 0 2px var(--red)' : 'none' }}>
                          <span style={{ fontSize: 12, fontWeight: 700 }}>{showVal}</span>
                          {code && code !== '1' && <span style={{ fontSize: 7, lineHeight: 1 }}>{statusShort(code)}</span>}
                        </div>
                      </div>;
                    })}
                    <div style={{ width: 34, textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>{sp}</div>
                  </div>
                  {/* Факт */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <div style={{ width: 58, fontSize: 11, color: 'var(--text-muted)', paddingLeft: 4 }}>Факт</div>
                    {weekDays.map(({ date }) => {
                      const f = factOf(ids, date); const p = planOf(ids, date);
                      const frame = f != null && p != null && f !== p;
                      return <div key={date} style={{ width: 34 }}><div style={{ ...cellBox, color: frame ? 'var(--red)' : 'var(--text-muted)', boxShadow: frame ? 'inset 0 0 0 2px var(--red)' : 'none' }}>{f == null ? '—' : f}</div></div>;
                    })}
                    <div style={{ width: 34, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>{sf}</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </>
      ) : (
        <HyperMonth stores={stores} daily={daily} weeks={weeks} weekNums={weekNums} mDetail={mDetail} setMDetail={setMDetail} />
      )}

      <Modal open={!!planEdit} onClose={() => setPlanEdit(null)} title={`Планирование · ${planEdit?.client || ''}`}>
        {planEdit && <PrognozEditor edit={planEdit} onClose={() => setPlanEdit(null)}
          onSave={(code, planVisits) => { onSavePlan({ pointIds: planEdit.pointIds, primaryId: planEdit.primaryId, date: planEdit.date, code, visits: code ? (planVisits ?? 0) : 0 }); setPlanEdit(null); }} />}
      </Modal>
    </div>
  );
}

function HyperMonth({ stores, daily, weeks, weekNums, mDetail, setMDetail }) {
  let tOrder = 0, tFact = 0;
  const blocks = stores.map(store => {
    const ids = store.pointIds;
    const weekCells = weekNums.map(w => (weeks[w]?.days || []).filter(d => !isWeekend(d.dow)).map(({ day, dow, date }) => {
      let order = 0; let plan = null, fact = null;
      ids.forEach(id => {
        const c = daily[id]?.[date]; if (!c) return;
        order += c.order || 0;
        if (c.plan != null) plan = (plan || 0) + c.plan;
        if (c.fact != null) fact = (fact || 0) + c.fact;
      });
      tOrder += order; if (fact != null) tFact += fact;
      const pct = order > 0 && fact != null ? Math.round(fact / order * 100) : (order > 0 ? null : undefined);
      return { day, dow, date, order, plan, fact, pct };
    }));
    return { store, weekCells };
  });
  const slAll = tOrder > 0 ? Math.round(tFact / tOrder * 100) : 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, margin: '2px 0 12px', flexWrap: 'wrap' }}>
        <span style={chip}>Заказ <b>{tOrder}</b></span>
        <span style={chip}>Факт <b>{tFact}</b></span>
        <span style={{ ...chip, background: slAll >= SL_TARGET ? 'var(--green-bg,#E4F2EA)' : 'var(--red-bg)', color: slAll >= SL_TARGET ? 'var(--green)' : 'var(--red)' }}>Service level {slAll}%</span>
      </div>
      {blocks.map(({ store, weekCells }) => (
        <Card key={store.ship_to} style={{ marginBottom: 8 }}>
          <div style={{ padding: '8px 12px 6px', fontSize: 12, fontWeight: 600 }}>{store.client}
            <span style={{ color: 'var(--text-light)', fontWeight: 400, fontSize: 10 }}> · {store.ship_to}</span>
            {store.pointIds.length > 1 && <span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 10, marginLeft: 6 }}>({store.pointIds.length} маршр.)</span>}
          </div>
          {store.address && <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-light)', padding: '0 12px 6px' }}>📍 {store.address}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 12px 10px' }}>
            {weekCells.map((cells, wi) => (
              <div key={wi} style={{ display: 'flex', gap: 2 }}>
                {cells.map(cell => {
                  const bg = cell.pct === undefined ? 'var(--surface2)' : cell.pct == null ? '#EDEDED' : cell.pct >= SL_TARGET ? '#E4F2EA' : 'var(--red-bg)';
                  const fg = cell.pct === undefined ? 'var(--text-light)' : cell.pct == null ? '#888' : cell.pct >= SL_TARGET ? 'var(--green)' : 'var(--red)';
                  return (
                    <div key={cell.date} onClick={() => setMDetail({ client: store.client, ...cell })}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
                      <div style={{ fontSize: 8, color: 'var(--text-light)', lineHeight: 1.15, marginBottom: 1, textAlign: 'center' }}>{DOW_RU[cell.dow]}<br/>{cell.day}</div>
                      <div style={{ width: 22, height: 26, borderRadius: 4, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600 }}>
                        {cell.pct === undefined ? '·' : cell.pct == null ? '–' : cell.pct}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      ))}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
        {mDetail ? (
          <span><b style={{ color: 'var(--text)' }}>{mDetail.client} · {parseInt(mDetail.date.slice(8),10)} {moShort(mDetail.date)}</b>
            <span style={{ marginLeft: 14 }}>Заказ <b>{mDetail.order}</b></span>
            <span style={{ marginLeft: 14 }}>План <b>{mDetail.plan == null ? '—' : mDetail.plan}</b></span>
            <span style={{ marginLeft: 14 }}>Факт <b>{mDetail.fact == null ? '—' : mDetail.fact}</b></span>
            <span style={{ marginLeft: 14 }}>SL <b>{mDetail.pct == null ? '—' : mDetail.pct + '%'}</b></span>
          </span>
        ) : <i>Кликните день — заказ / план / факт / SL%.</i>}
      </div>
    </div>
  );
}

// ── ВКЛАДКА ОТПУСКА ──────────────────────────────────────────────────────────
function VacationTab({ routes, vacations, period, userId, onUpdate }) {
  const [pickerOpen, setPickerOpen] = useState(null); // route_id
  const [selecting, setSelecting]   = useState(null); // { from, to, hover }
  const [saving, setSaving]         = useState(false);
  const [banner, setBanner]         = useState({ show: false, msg: '' });

  // Месяц активного периода + СЛЕДУЮЩИЙ месяц (для переходящих отпусков).
  const PY = period?.year || 2026;
  const PM = period?.month || 9;                 // 1..12
  const DOW_HDRS = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];
  const isoOf = (y, m, d) => `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  // Модель месяца: { title, offset(Пн=0), cells:[{day, iso, weekend}] }.
  function monthModel(y, m) {
    const days = new Date(y, m, 0).getDate();
    const offset = (new Date(y, m - 1, 1).getDay() + 6) % 7;
    const cells = [];
    for (let d = 1; d <= days; d++) {
      const dow = (new Date(y, m - 1, d).getDay() + 6) % 7;
      cells.push({ day: d, iso: isoOf(y, m, d), weekend: dow >= 5 });
    }
    return { title: `${MONTHS_RU[m - 1]} ${y}`, offset, cells };
  }
  const nextY = PM === 12 ? PY + 1 : PY;
  const nextM = PM === 12 ? 1 : PM + 1;
  const months = [monthModel(PY, PM), monthModel(nextY, nextM)];   // +1 месяц вперёд

  function vacFor(routeId) { return vacations.find(v => v.route_id === routeId); }

  // #5 — фактические (календарные) дни, не рабочие.
  function actualDays(fromISO, toISO) {
    if (!fromISO || !toISO) return 0;
    return Math.round((new Date(toISO) - new Date(fromISO)) / 86400000) + 1;
  }
  const dLabel = (iso) => `${parseInt(iso.slice(8), 10)} ${moShort(iso)}`;

  function openPicker(routeId) {
    const v = vacFor(routeId);
    setSelecting(v ? { from: v.date_from, to: v.date_to } : { from: null, to: null });
    setPickerOpen(routeId);
  }

  // #4 — выходные кликабельны; конец может быть в следующем месяце. Работаем по ISO-датам.
  function dayClick(iso) {
    setSelecting(prev => {
      if (!prev?.from || (prev.from && prev.to)) return { from: iso, to: null };
      const [f, t] = prev.from <= iso ? [prev.from, iso] : [iso, prev.from];
      return { from: f, to: t };
    });
  }

  async function saveVacation() {
    if (!selecting?.from || !selecting?.to || !pickerOpen) return;
    setSaving(true);
    try {
      const { setVacation } = await import('../lib/data');
      const saved = await setVacation({
        periodId: period.id, routeId: pickerOpen,
        dateFrom: selecting.from, dateTo: selecting.to,   // уже ISO (могут быть в разных месяцах)
        workingDays: actualDays(selecting.from, selecting.to),  // фактические дни
        userId,
      });
      onUpdate(prev => {
        const filtered = prev.filter(v => v.route_id !== pickerOpen);
        return [...filtered, { ...saved, route_id: pickerOpen }];
      });
      setPickerOpen(null);
      setBanner({ show: true, msg: '📅 Отпуск сохранён' });
      setTimeout(() => setBanner({ show: false, msg: '' }), 2200);
    } catch (err) {
      setBanner({ show: true, msg: '⚠ Ошибка: ' + err.message });
    } finally {
      setSaving(false);
    }
  }

  async function removeVacation(routeId) {
    try {
      const { deleteVacation } = await import('../lib/data');
      await deleteVacation(period.id, routeId);
      onUpdate(prev => prev.filter(v => v.route_id !== routeId));
      setBanner({ show: true, msg: '✕ Отпуск удалён' });
      setTimeout(() => setBanner({ show: false, msg: '' }), 2200);
    } catch (err) { console.error(err); }
  }

  // Конфликты
  const conflicts = [];
  const withVac = routes.filter(r => vacFor(r.id));
  for (let i = 0; i < withVac.length; i++) {
    for (let j = i+1; j < withVac.length; j++) {
      const a = vacFor(withVac[i].id), b = vacFor(withVac[j].id);
      if (a.date_from <= b.date_to && b.date_from <= a.date_to) {
        const ov_from = a.date_from > b.date_from ? a.date_from : b.date_from;
        const ov_to   = a.date_to   < b.date_to   ? a.date_to   : b.date_to;
        conflicts.push({ mrc1: getMrc(withVac[i])?.full_name, mrc2: getMrc(withVac[j])?.full_name, from: ov_from, to: ov_to });
      }
    }
  }

  // Считаем суммарные дни пересечения (по уникальным дням, не по парам)
  const routeIds = routes.filter(r => !r.is_vacancy).map(r => r.id);
  const dayCounts = {};
  withVac.forEach(r => {
    const v = vacFor(r.id);
    const from = new Date(v.date_from), to = new Date(v.date_to);
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split('T')[0];
      dayCounts[key] = (dayCounts[key] || 0) + 1;
    }
  });
  const overlapDaysCount = Object.values(dayCounts).filter(c => c >= 2).length;

  return (
    <div>
      {overlapDaysCount > 0 && (
        <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 8, padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--red)' }}>{overlapDaysCount}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)' }}>дней с пересечением отпусков</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>2 и более МРЧ одновременно отсутствуют</div>
          </div>
        </div>
      )}

      {conflicts.length > 0 && (
        <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: 'var(--red)', fontWeight: 600 }}>
          ⚠️ Пересечения отпусков: {conflicts.length}
          {conflicts.map((c, i) => (
            <div key={i} style={{ fontWeight: 400, color: 'var(--text)', marginTop: 4, fontSize: 12 }}>
              {c.mrc1} и {c.mrc2} — {c.from.slice(8)}.{c.from.slice(5,7)}–{c.to.slice(8)}.{c.to.slice(5,7)}
            </div>
          ))}
        </div>
      )}

      <SectionTitle>Календарь пересечений</SectionTitle>
      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: 12 }}>
          <VacationCalendar
            routes={routes}
            vacations={vacations}
            monthPrefix={period?.date_from?.slice(0, 7)}
            daysInMonth={period ? new Date(period.year, period.month, 0).getDate() : 30}
            offset={period ? new Date(period.year, period.month - 1, 1).getDay() : 1}
            onOpenRoute={(routeId) => {
              document.getElementById(`vacrow-${routeId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              openPicker(routeId);
            }}
          />
        </div>
      </Card>

      <div style={{ background: '#FFFDE7', border: '1px solid #F9A825', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#7D5A00' }}>
        📅 Нажмите «Выбрать даты» — выберите первый и последний день отпуска
      </div>

      <Card>
        {routes.filter(r => !r.is_vacancy).map(route => {
          const mrc = getMrc(route);
          const v = vacFor(route.id);
          const isOpen = pickerOpen === route.id;

          return (
            <div key={route.id} id={`vacrow-${route.id}`} style={{ borderBottom: '1px solid var(--border)', padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{mrc?.full_name}
                    {route.is_pm && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#6B46C1', background: '#EDE7F6', borderRadius: 4, padding: '1px 5px' }}>ПМ</span>}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{route.internal_code}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {v && (
                    <>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
                        background: v.status === 'approved' ? '#FFF9C4' : v.status === 'rejected' ? '#F0F0F0' : '#E5E5E5',
                        color: v.status === 'approved' ? '#7D5A00' : v.status === 'rejected' ? '#9C0006' : '#666',
                        border: `1px solid ${v.status === 'approved' ? '#F9A825' : '#BBB'}`,
                        opacity: v.status === 'pending' ? 0.85 : 1,
                      }}>
                        📅 {v.date_from.slice(8)}.{v.date_from.slice(5,7)}–{v.date_to.slice(8)}.{v.date_to.slice(5,7)} · {v.working_days} р.д.
                        {v.status === 'pending' && ' · не подтверждено'}
                        {v.status === 'rejected' && ' · отклонено'}
                      </span>
                      <button onClick={() => removeVacation(route.id)} style={{ background: 'none', border: 'none', color: 'var(--text-light)', fontSize: 16, cursor: 'pointer' }}>✕</button>
                    </>
                  )}
                </div>
              </div>
              {v && v.status === 'rejected' && v.rejected_reason && (
                <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 8 }}>Причина: {v.rejected_reason}</div>
              )}

              <button onClick={() => isOpen ? setPickerOpen(null) : openPicker(route.id)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 6, border: '1.5px dashed #F9A825',
                background: '#FFFDE7', color: '#7D5A00', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
                📅 {v ? 'Изменить даты' : 'Выбрать даты'}
              </button>

              {isOpen && (
                <div style={{ marginTop: 10, background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)', padding: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 8 }}>
                    {!selecting?.from ? 'Выберите первый день' : !selecting?.to ? `Начало: ${dLabel(selecting.from)} — выберите последний день` : `${dLabel(selecting.from)} – ${dLabel(selecting.to)} · ${actualDays(selecting.from, selecting.to)} дн.`}
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {months.map((mm, mi) => (
                      <div key={mi} style={{ flex: '1 1 220px', minWidth: 220 }}>
                        <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: mi === 0 ? 'var(--text)' : 'var(--accent)', textTransform: 'capitalize', marginBottom: 4 }}>
                          {mm.title}{mi === 1 && ' · след. месяц'}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
                          {DOW_HDRS.map((d, i) => (
                            <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: i >= 5 ? '#E53E3E' : 'var(--text-muted)', padding: '2px 0' }}>{d}</div>
                          ))}
                          {Array.from({ length: mm.offset }).map((_, i) => <div key={`e${mi}-${i}`} />)}
                          {mm.cells.map(({ day, iso, weekend }) => {
                            const isStart = iso === selecting?.from;
                            const isEnd   = iso === selecting?.to;
                            const inRange = selecting?.from && selecting?.to && iso > selecting.from && iso < selecting.to;
                            return (
                              <div key={iso} onClick={() => dayClick(iso)} style={{
                                textAlign: 'center', fontSize: 12,
                                padding: '6px 2px', borderRadius: 5, cursor: 'pointer',
                                color: (isStart || isEnd) ? '#fff' : weekend ? '#E53E3E' : 'var(--text)',
                                background: (isStart || isEnd) ? '#F9A825' : inRange ? '#FFF9C4' : 'transparent',
                                fontWeight: (isStart || isEnd) ? 700 : 500,
                              }}>
                                {day}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <Btn size="md" color="ghost" onClick={() => setPickerOpen(null)} style={{ flex: 1 }}>Отмена</Btn>
                    <Btn size="md" color="yellow" onClick={saveVacation} disabled={!selecting?.from || !selecting?.to || saving} style={{ flex: 1, background: '#F9A825' }}>
                      {saving ? 'Сохранение...' : 'Сохранить'}
                    </Btn>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </Card>

      <SaveBanner show={banner.show} message={banner.msg} />
    </div>
  );
}

// ── ВКЛАДКА ИТОГИ ────────────────────────────────────────────────────────────
function SummaryTab({ routes, statuses, orders = {}, statusesV = {}, factsMap = {}, weeks, weekNums }) {
  const wc = WEEK_COLORS;
  // Вакансии учитываем в сводке и SL наравне с укомплектованными — заказ на них
  // реален, и так «Вся команда» сходится с «Итог по дням» в табеле.
  const teamRoutes = routes;

  // Сводная по всей команде — модель Заказ / Прогноз / Факт (без «выходов»)
  const teamWeekTotals = {};
  weekNums.forEach(w => {
    const days = weeks[w]?.days || [];
    const acc = { order: 0, plan: 0, fact: 0, sick: 0, vacation: 0, dayoff: 0, overlaps: 0 };
    teamRoutes.forEach(r => {
      days.forEach(({ date }) => {
        const ordV = orders[r.id]?.[date]?.visits ?? 0;
        acc.order += ordV;
        const pv = planVisitsOf(statusesV[r.id]?.[date], ordV);
        if (pv != null) acc.plan += pv;
        const f = factsMap[r.id]?.[date];
        if (f != null) acc.fact += f;
      });
      const m = calcMetrics(statuses, r.id, days);
      acc.sick += m.sick; acc.vacation += m.vacation; acc.dayoff += m.dayoff; acc.overlaps += m.overlaps;
    });
    acc.planPct = acc.order > 0 ? Math.round(acc.plan / acc.order * 100) : null;   // % Планирование к заказу
    acc.slPct   = acc.order > 0 ? Math.round(acc.fact / acc.order * 100) : null;   // SL = Факт / Заказ
    teamWeekTotals[w] = acc;
  });

  return (
    <div>
      <SectionTitle>Вся команда</SectionTitle>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {weekNums.map(w => {
              const t = teamWeekTotals[w];
              const c = wc[w] || wc[36];
              const num = (k, v, col) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '1px 0' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{k}</span><span style={{ fontWeight: 700, color: col || 'var(--text)' }}>{v}</span>
                </div>
              );
              return (
                <div key={w} style={{ flex: 1, minWidth: 96, padding: '10px 9px', borderRadius: 8, background: c.light }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: c.hdr, textAlign: 'center', marginBottom: 6 }}>W{w}</div>
                  {num('Заказ', t.order)}
                  {num('Прогноз', t.plan)}
                  {num('Факт', t.fact, 'var(--green)')}
                  <div style={{ borderTop: `1px solid ${c.hdr}30`, margin: '5px 0', paddingTop: 5 }}>
                    {num('План/Заказ', t.planPct == null ? '—' : t.planPct + '%', t.planPct != null && t.planPct < SL_TARGET ? 'var(--red)' : c.hdr)}
                    {num('SL', t.slPct == null ? '—' : t.slPct + '%', t.slPct != null && t.slPct < SL_TARGET ? 'var(--red)' : c.hdr)}
                  </div>
                  {(t.sick||t.vacation||t.dayoff||t.overlaps) > 0 && (
                    <div style={{ fontSize: 9.5, color: 'var(--text-light)', marginTop: 3, textAlign: 'center' }}>
                      {t.sick ? `БЛ ${t.sick} ` : ''}{t.vacation ? `ОТ ${t.vacation} ` : ''}{t.dayoff ? `ДО ${t.dayoff} ` : ''}{t.overlaps ? `↔${t.overlaps}` : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* #10 — по дням у СВ показываем визиты (Заказ/Прогноз/Факт/SL), а не «выходы» */}
      <SectionTitle>По дням — вся команда</SectionTitle>
      {weekNums.map(w => (
        <DaySumPanel key={w} routes={teamRoutes} orders={orders} statusesV={statusesV} factsMap={factsMap}
          weekDays={weeks[w]?.days || []} weekNo={w} />
      ))}

      <SectionTitle>По каждому МРЧ</SectionTitle>
      {routes.filter(r => !r.is_vacancy).map(route => {
        const mrc = getMrc(route);
        return (
          <Card key={route.id}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{mrc?.full_name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{route.internal_code}</div>
            </div>
            <div style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {weekNums.map(w => {
                  const days = weeks[w]?.days || [];
                  const c = wc[w] || wc[36];
                  let order = 0, plan = 0, fact = 0;
                  days.forEach(({ date }) => {
                    const ordV = orders[route.id]?.[date]?.visits ?? 0;
                    order += ordV;
                    const pv = planVisitsOf(statusesV[route.id]?.[date], ordV);
                    if (pv != null) plan += pv;
                    const f = factsMap[route.id]?.[date];
                    if (f != null) fact += f;
                  });
                  const planPct = order > 0 ? Math.round(plan / order * 100) : null;
                  const slPct   = order > 0 ? Math.round(fact / order * 100) : null;
                  const num = (k, v, col) => (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, padding: '1px 0' }}>
                      <span style={{ color: 'var(--text-muted)' }}>{k}</span><span style={{ fontWeight: 700, color: col || 'var(--text)' }}>{v}</span>
                    </div>
                  );
                  return (
                    <div key={w} style={{ flex: 1, minWidth: 92, padding: '8px 8px', borderRadius: 8, background: c.light }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: c.hdr, textAlign: 'center', marginBottom: 5 }}>W{w}</div>
                      {num('Заказ', order)}
                      {num('Прогноз', plan)}
                      {num('Факт', fact, 'var(--green)')}
                      <div style={{ borderTop: `1px solid ${c.hdr}30`, margin: '4px 0', paddingTop: 4 }}>
                        {num('План/Зак', planPct == null ? '—' : planPct + '%', planPct != null && planPct < SL_TARGET ? 'var(--red)' : c.hdr)}
                        {num('SL', slPct == null ? '—' : slPct + '%', slPct != null && slPct < SL_TARGET ? 'var(--red)' : c.hdr)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
